/**
 * ABOUT tab — README §1: optional alert callout, THE PARTY description panel with link
 * chips, 4-up stats strip, HOUSE RULES numbered list, MEDIA 5-up grid (+N opens the viewer).
 * Mobile (§2): callout, description, host row, 3-up media grid, house rules.
 */
import React from 'react';
import { Panel, PanelHeader, PanelBody, StatStrip, Callout, SUBTLE, MUTED, SOFT } from '../surface';
import Linkify from '../Linkify';
import { LinksPills } from '../LinksEditor';

function Description({ event, mobile }) {
  if (!event?.description) return null;
  const isHtml = /[<][a-z/]/i.test(event.description);
  return (
    <>
      {isHtml ? (
        // Rich-text description. Server-sanitized (on both write and read), so it's safe to
        // render — and the .prose-event styles force the app's monospace font.
        <div className="prose-event text-gray-800" style={{ fontSize: mobile ? 13 : 14, lineHeight: mobile ? 1.7 : 1.75 }} dangerouslySetInnerHTML={{ __html: event.description }} />
      ) : (
        <p className="whitespace-pre-wrap font-mono" style={{ fontSize: mobile ? 13 : 14, lineHeight: mobile ? 1.7 : 1.75, margin: 0, textWrap: 'pretty' }}>
          <Linkify text={event.description} linkClassName="underline text-blue-700" />
        </p>
      )}
      {event.links?.length > 0 && <LinksPills links={event.links} />}
      {/* Attribution for ingested listings. The description is our own wording (see
          SeoRewriteService) — crediting where the facts came from is the right thing to do,
          and rel="nofollow" keeps it from reading as a link exchange. */}
      {event.sourceDomain && (
        <p className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: SUBTLE, margin: 0 }} data-testid="source-attribution">
          Source:{' '}
          {event.sourceAttributionUrl ? (
            <a href={event.sourceAttributionUrl} target="_blank" rel="nofollow noopener noreferrer" className="underline" style={{ color: MUTED }}>{event.sourceDomain}</a>
          ) : event.sourceDomain}
        </p>
      )}
    </>
  );
}

function Rules({ rules, mobile }) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  return (
    <Panel dense={mobile} data-testid="house-rules">
      <PanelHeader label="HOUSE RULES" dense={mobile} right={`${rules.length} ${rules.length === 1 ? 'RULE' : 'RULES'}`} />
      <div className="flex flex-col" style={{ gap: mobile ? 8 : 10 }}>
        {rules.map((rule, i) => (
          <div key={rule.id || i} className="flex items-start gap-[10px]">
            <span className="flex flex-shrink-0 items-center justify-center rounded-full font-mono font-bold" style={{ width: 26, height: 26, background: SOFT.chip, fontSize: 10 }}>{i + 1}</span>
            <span className="font-mono" style={{ fontSize: mobile ? 12 : 13, lineHeight: 1.6, paddingTop: 4 }}>{rule.text}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function MediaGrid({ items = [], onOpen, cols = 5, mobile = false, withPanel = true }) {
  if (items.length === 0) return null;
  const visible = items.length > cols ? items.slice(0, cols - 1) : items;
  const extra = items.length - visible.length;
  const photos = items.filter((m) => m.kind === 'image' || !m.kind).length;
  const videos = items.length - photos;
  const grid = (
    <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: mobile ? 4 : 6 }} data-testid="media-grid">
      {visible.map((m, i) => {
        const thumb = m.thumbnailUrl || m.url;
        return (
          <button key={m.id || `${i}-${thumb}`} type="button" onClick={() => onOpen?.(i)} className="relative overflow-hidden bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2" style={{ aspectRatio: '1', borderRadius: mobile ? 12 : 14, border: 'none', padding: 0 }} aria-label={`Open media ${i + 1}`}>
            {thumb ? <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" /> : <span className="flex h-full w-full items-center justify-center font-mono text-white" style={{ fontSize: 10 }}>MEDIA</span>}
            {m.kind && m.kind !== 'image' && <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono font-bold text-white" style={{ fontSize: 18, textShadow: '0 0 6px #000' }}>▶</span>}
          </button>
        );
      })}
      {extra > 0 && (
        <button type="button" onClick={() => onOpen?.(visible.length)} className="flex items-center justify-center bg-black font-mono font-bold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2" style={{ aspectRatio: '1', borderRadius: mobile ? 12 : 14, border: 'none', fontSize: mobile ? 16 : 18 }} aria-label={`${extra} more`} data-testid="media-more">+{extra}</button>
      )}
    </div>
  );
  if (!withPanel) return grid;
  const note = [photos > 0 ? `${photos} ${photos === 1 ? 'PHOTO' : 'PHOTOS'}` : null, videos > 0 ? `${videos} ${videos === 1 ? 'VIDEO' : 'VIDEOS'}` : null].filter(Boolean).join(' · ');
  return (
    <Panel dense={mobile} data-testid="media-panel">
      <PanelHeader label="MEDIA" right={note} dense={mobile} />
      {grid}
    </Panel>
  );
}

export default function EventAboutTab({ event, model, mobile = false, items = [], onOpenPhoto, before = null, after = null }) {
  const callout = model.callout && (
    <Callout tone={model.callout.tone} title={model.callout.title} mobile={mobile} testId="about-callout">{model.callout.body}</Callout>
  );
  if (mobile) {
    // The identity card above already carries the host pill, so the ABOUT tab is the party,
    // the pictures and the rules.
    return (
      <div className="flex flex-col" style={{ gap: 10 }} data-testid="about-tab">
        {before}
        {callout}
        <Panel dense data-testid="the-party">
          <PanelHeader label="THE EVENT" dense />
          {event?.description ? <Description event={event} mobile /> : <p className="font-mono" style={{ fontSize: 12, color: MUTED, margin: 0 }}>The host has not written anything yet.</p>}
        </Panel>
        {items.length > 1 && <MediaGrid items={items} onOpen={onOpenPhoto} cols={3} mobile />}
        <Rules rules={event?.houseRules} mobile />
        {after}
      </div>
    );
  }
  return (
    <div className="flex flex-col" style={{ gap: 14 }} data-testid="about-tab">
      {before}
      {callout}
      <Panel data-testid="the-party">
        <PanelHeader label="THE EVENT" right={model.descNote} />
        <PanelBody>
          {event?.description ? <Description event={event} /> : <p className="font-mono" style={{ fontSize: 13, color: MUTED, margin: 0 }}>The host has not written anything yet.</p>}
        </PanelBody>
      </Panel>
      <StatStrip stats={model.stats} testId="about-stats" />
      <Rules rules={event?.houseRules} />
      {items.length > 1 && <MediaGrid items={items} onOpen={onOpenPhoto} cols={5} />}
      {after}
    </div>
  );
}
